/* DataExplorerResultsViewComponent.js */

"use strict";
const _ = require("lodash");
const EventEmitter = require("events").EventEmitter;
const Renderer = require("./DataExplorerResultsViewRenderer");

const EmailComponent = require("./veils/EmailComponent");
const moment = require("moment-timezone");
const DownloadComponent = require("./veils/download/DownloadComponent");
const EmailRegex = new RegExp(
    /^(.*\s<(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))>)|((([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))$/
);

class DataExplorerResultsViewComponent extends EventEmitter {
    constructor(logger, gameEditor, userSession, MUF, ExplorerTemplate, Explorables, steveIo) {
        super();
        this._logger = logger;
        this._gameEditor = gameEditor;
        this._userSession = userSession;
        this._modalUserFeedback = MUF;
        this._ExplorerTemplate = ExplorerTemplate;
        this._templateEntity = undefined;
        this._Explorables = Explorables;
        this._fileFactory = steveIo.fileFactory;
        this._explorablesEntity = undefined;
        this._osImage = undefined;
        this._activeOrgId = userSession.activeOrganization.id;
        this._rowOrientationKey = undefined;
        this._updatedRowOrientationKey = undefined;
        this._isOwnUser = false; // change to false to enable logic
        this._rowOrientationGroups = undefined;

        this._hasRunPerm = false;
        this._renderer = new Renderer(this);
        this._onPristinePropertiesChanged = this._onPristinePropertiesChanged.bind(this);
        this._registerListeners();
    }

    get templateEntity() {
        return this._templateEntity;
    }

    get isDirty() {
        return this._templateEntity ? this._templateEntity.isDirty() : false;
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS   */
    /*******************************************/

    _registerListeners() {}

    _unregisterListeners() {}

    _registerTemplateListeners() {
        this._unRegisterTemplateListeners();
        this._templateEntity.on("PristinePropertiesChanged", this._onPristinePropertiesChanged);
    }

    _unRegisterTemplateListeners() {
        this._templateEntity.removeListener(
            "PristinePropertiesChanged",
            this._onPristinePropertiesChanged
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS     */
    /* START PUBLIC COMPONENT MODEL METHODS    */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    get gameEditor() {
        return this.getGameEditor();
    }
    getGameEditor() {
        return this._gameEditor;
    }

    shutdown() {
        if (!_.isUndefined(this._templateEntity)) {
            this._unRegisterTemplateListeners();
        }
        this._unregisterListeners();
        return this._renderer.shutdown().then(
            () => {
                this._osImage = undefined;
                this._logger = undefined;
                this._gameEditor = undefined;
                this._renderer = undefined;
            },
            (err) => {
                this._logger.warn(err, "DataExplorerResultsViewComponent.shutdown() error.");
            }
        );
    }

    get logger() {
        return this.getLogger();
    }
    getLogger() {
        return this._logger;
    }

    /*******************************************/
    /* END COMPONENT MODEL METHODS             */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _onPristinePropertiesChanged(pristineProperties, before) {
        if (pristineProperties.lastExecutedOn > before.lastExecutedOn && !this._isReRun) {
            this._templateEntity
                .loadRelatedExecutions({
                    ownerUserId: _.get(this._userSession, "user.id"),
                    tid: _.get(this._userSession, "activeOrganization.id"),
                })
                .then((exec) => {
                    if (this._execution && exec[0].id === this._execution.id) {
                        throw "done"; // stopping the flow if it is same execution entity
                    }
                    return this._modalUserFeedback.displayConfirmation(
                        "Newer results are available. Would you like to refresh?",
                        {
                            title: "Updated results available",
                            confirmDetails: {
                                text: "OK",
                            },
                            rejectDetails: {
                                text: "Cancel",
                            },
                        }
                    );
                })
                .then(() => {
                    this._renderer.render(this);
                })
                .catch((err) => {
                    if (err === "done") {
                        return;
                    }
                    if (!err.userRejected) {
                        this._logger.warn("Result update error", err);
                    }
                });
        }
        this.emit("TemplatePropertiesChanged", pristineProperties, before);
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE METHODS                   */
    /*******************************************/

    _isValidEmail(email) {
        return EmailRegex.test(email);
    }

    /*******************************************/
    /* END PRIVATE METHODS                     */
    /* START PUBLIC API METHODS                */
    /*******************************************/

    whenModelIsReady() {
        // console.time("ModelIsReadyStart");
        // console.time("EntityRetrival");
        return this._gameEditor
            .whenEditorIsReady()
            .then(() => {
                if (
                    _.get(this._userSession, "authorizedActions", []).indexOf(
                        "dataExplorerExploreData"
                    ) >= 0
                ) {
                    this._hasRunPerm = true;
                }
                this._templateEntity = this._gameEditor.template;
                return this._templateEntity.loadRelatedExecutions({
                    ownerUserId: _.get(this._userSession, "user.id"),
                    tid: _.get(this._userSession, "activeOrganization.id"),
                });
            })
            .then((exec) => {
                this._execution = exec[0];
                this._registerTemplateListeners();
                if (!this._execution) {
                    return Promise.reject("notexecuted");
                }
                if (this._execution.status === "RUNNING") {
                    return Promise.reject("running");
                }
                if (this._execution.status === "FAILED") {
                    return Promise.reject("failed");
                }
                return exec[0].loadRelatedResults();
            })
            .then((res) => {
                // console.timeEnd("EntityRetrival");
                this._results = res;
                // console.timeEnd("ModelIsReadyStart");
                return Promise.resolve("ok");
            })
            .catch((err) => {
                // console.timeEnd("EntityRetrival");
                // console.log(err);
                return Promise.resolve(err);
            });
    }

    dataToInt8Arr(data) {
        const byteString = window.atob(data);
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const int8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
            int8Array[i] = byteString.charCodeAt(i);
        }
        return int8Array;
    }

    downloadTemplate() {
        let _type = "csv";
        const _tooLargeForTable = !!_.get(this._renderer, "_tooLargeForTable");
        return this._modalUserFeedback
            .displayCustom(new DownloadComponent(this._logger), {dontHide: true})
            .then((type) => {
                this._modalUserFeedback.displayLoading();
                _type = type;
                return this._execution.loadRelatedWork();
            })
            .then((work) => work.loadRelatedReports({reportType: _type}))
            .then((rep) => {
                const _templateName = this.templateEntity.name,
                    _execDate = new Date(this._execution.executedOn);
                const timeZone = moment.tz.guess(),
                    _fileNameTime = moment
                        .tz(_execDate.toISOString(), timeZone)
                        .format("YYYYMMDD_HHmmss_z"),
                    _fileName = `DE_Results_${_templateName}_${_fileNameTime}`;
                const _useBackendPdf = _type === "pdf" && _tooLargeForTable;
                return this._modalUserFeedback.hideDisplayed().then(() => {
                    const report = _.get(rep, "[0].report");
                    if ((_type !== "pdf" || _useBackendPdf) && _.isNil(report)) {
                        throw this._noReportError();
                    }
                    switch (_type) {
                        case "json":
                            this._fileFactory.saveJsonAsFile(report.results, _fileName + ".json");
                            break;
                        case "pdf":
                            if (_useBackendPdf) {
                                this._fileFactory.saveArrayBufferAsFile(
                                    this.dataToInt8Arr(report),
                                    _fileName + ".pdf",
                                    "application/pdf"
                                );
                            }
                            break;
                        default:
                            this._fileFactory.saveStringAsFile(
                                report,
                                _fileName + ".csv",
                                "text/csv"
                            );
                            break;
                    }
                    return Promise.resolve({
                        type: _type,
                        fname: _fileName,
                        backendPdf: _useBackendPdf,
                    });
                });
            })
            .catch((err) => {
                this._logger.warn("Download Error::", err);
                return this._modalUserFeedback.hideDisplayed().then(() => {
                    if (!err.userRejected) {
                        this._modalUserFeedback.displayError(err);
                    }
                });
            });
    }

    /**
     * User-facing error when the work has no downloadable report associated with it (PE-14187)
     * @returns {{userFacing: boolean, title: string, errorCode: string, errorMsg: string}}
     */
    _noReportError() {
        return {
            userFacing: true,
            title: "Report Unavailable",
            errorCode: "DE_NO_REPORT",
            errorMsg:
                "No downloadable report is available for this result. The result set may be " +
                "too large to export, or report generation did not complete. Try narrowing " +
                "the data selection and running the template again.",
        };
    }

    emailReport() {
        let recipient, _type;
        const _templateName = this.templateEntity.name,
            _execDate = new Date(this._execution.executedOn),
            _dateTime = _execDate.toLocaleString(),
            _uname = _.get(this._userSession, "user.userName", ""),
            _fname = `${_.get(this._userSession, "user.firstName", "")} ${_.get(
                this._userSession,
                "user.lastName",
                ""
            )}`,
            _genBy = `${_fname} (${_uname})`,
            _genIn = _.get(this._userSession, "activeOrganization.name", "");
        const timeZone = moment.tz.guess(),
            _fileNameTime = moment
                .tz(_execDate.toISOString(), timeZone)
                .format("YYYYMMDD_HHmmss_z"),
            _tzone = moment.tz(_execDate.toISOString(), timeZone).format("z"),
            _fileName = `DE_Results_${_templateName}_${_fileNameTime}`;
        const _uemail = _.get(this._userSession, "user.email");

        return this._modalUserFeedback
            .displayCustom(new EmailComponent(this._logger, _uemail), {dontHide: true})
            .then(({name, type}) => {
                recipient = name;
                _type = type;
                return this._modalUserFeedback.hideDisplayed();
            })
            .then(() => {
                return this._execution.loadRelatedWork();
            })
            .then((work) => {
                // emailWorkReport(recipient, subject, reportName, body)
                const body =
                    `<p><b class="bold">Template name:</b> ${_templateName}</p>` +
                    `<p><b class="bold">Generated on:</b> ${_dateTime} (${_tzone})</p>` +
                    `<p><b class="bold">Generated by:</b> ${_genBy}</p>` +
                    `<p><b class="bold">Organization:</b> ${_genIn}</p>`;
                if (_type === "pdf") {
                    return this._renderer._generateKendoPdf("email").then((data) => {
                        return Promise.resolve({
                            work,
                            body,
                            report: data,
                        });
                    });
                } else {
                    return Promise.resolve({
                        work,
                        body,
                    });
                }
            })
            .then((mailData) => {
                const {work, body, report} = mailData;
                if (!report) {
                    return work.emailWorkReport(
                        recipient,
                        `${_templateName} ${_dateTime} (${_tzone})`,
                        _fileName,
                        body,
                        _type
                    );
                }
                const _report = report.split("base64,")[1];
                return work.constructor.emailReport(
                    recipient,
                    `${_templateName} ${_dateTime} (${_tzone})`,
                    _fileName,
                    body,
                    _type,
                    _report
                );
            })
            .catch((err) => {
                this._logger.warn(err);
                return this._modalUserFeedback.hideDisplayed().then(() => {
                    if (!err.userRejected) {
                        this._modalUserFeedback.displayError(err);
                    }
                });
            });
    }

    _runWork() {
        let _work;
        return this._execution
            .loadRelatedWork()
            .then((work) => {
                _work = work;
                return this._modalUserFeedback.displayLoading({message: "Running the report... "});
            })
            .then(() => {
                let workInpDesc;
                if (_work.workInput.description) {
                    workInpDesc = _work.workInput.description.split("-");
                    workInpDesc = workInpDesc.slice(0, workInpDesc.length - 1);
                    workInpDesc = `${workInpDesc.join(
                        "-"
                    )} ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
                } else {
                    workInpDesc = `Explore Data - ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
                }

                const workInp = Object.assign({}, _work.workInput, {description: workInpDesc});
                return this.templateEntity.runExploreWork(
                    _work.workTarget,
                    workInp,
                    {},
                    (progress) => {
                        // console.log(progress);
                        const percent = parseInt(
                            (typeof progress.targetCount === "number" && progress.targetCount !== 0
                                ? (progress.successCount +
                                      progress.failureCount +
                                      progress.missingTargetCount) /
                                  progress.targetCount
                                : 0) * 100,
                            10
                        );
                        this._modalUserFeedback.showLoadingProgress(`${percent}%`);
                    }
                );
            });
    }

    /*******************************************/
    /* END PUBLIC API METHODS                  */
    /*******************************************/
}

module.exports = DataExplorerResultsViewComponent;

DataExplorerResultsViewComponent.$inject = [
    "$teve.logger",
    "gameEditor.gameEditor",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.entities.ExplorerTemplate",
    "$teve.entities.Explorables",
    "$teve.io",
];

DataExplorerResultsViewComponent.$displayDetails = {
    type: "View",
    name: "Data Explorer Results",
    icon: "common.results_link",
    shown: true,
    title: "Data Explorer Results",
    order: 1,
};
